import { useMemo, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import { errorMessage } from '../../lib/errorMessage';
import { ProjectDrawer } from '../delivery/projects/ProjectDrawer';
import { projectStatusLabel } from '../delivery/projects/projects.schema';
import { formatILS } from '../finance/projectFinance';
import { useProjectsFinance } from '../finance/projectFinanceQueries';
import { formatDuration } from '../hours/hoursMath';
import { useProjectsMinutes } from '../hours/hoursQueries';
import { buildCustomerBook, type BookGroup, type Rollup } from './customerBook';
import type { CustomerProjectRow } from './customerProfileQueries';
import { useDeleteProductLine, useProductLines, useUpsertProductLine } from './productLinesQueries';
import styles from './CustomerProfile.module.css';

function RollupLine({ rollup }: { rollup: Rollup }) {
  return (
    <div className={styles.rollup}>
      <span>
        ערך{' '}
        <strong>
          <bdi>{formatILS(rollup.value)}</bdi>
        </strong>
      </span>
      <span>
        התקבל{' '}
        <strong>
          <bdi>{formatILS(rollup.received)}</bdi>
        </strong>
      </span>
      <span>
        נשאר לקבל{' '}
        <strong>
          <bdi>{formatILS(rollup.outstanding)}</bdi>
        </strong>
      </span>
      <span>
        שעות{' '}
        <strong>
          <bdi>{formatDuration(rollup.minutes)}</bdi>
        </strong>
      </span>
    </div>
  );
}

interface Props {
  companyId: string;
  companyName: string;
  projects: CustomerProjectRow[];
  orgId: string;
  userId: string | null;
}

export function CustomerProjectsTab({ companyId, companyName, projects, orgId, userId }: Props) {
  const toast = useToast();
  const { data: allLines = [] } = useProductLines(orgId);
  const lines = useMemo(
    () => allLines.filter((l) => l.company_id === companyId),
    [allLines, companyId],
  );
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { data: finance } = useProjectsFinance(orgId, projectIds);
  const { data: minutes = {} } = useProjectsMinutes(orgId, projectIds);
  const upsertLine = useUpsertProductLine(orgId, userId);
  const deleteLine = useDeleteProductLine(orgId);

  const [newLine, setNewLine] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [drawer, setDrawer] = useState<{ open: boolean; lineId: string | null }>({
    open: false,
    lineId: null,
  });

  const book = buildCustomerBook(
    projects,
    lines,
    finance?.invoices ?? [],
    finance?.payments ?? [],
    minutes,
  );

  async function addLine(event: React.FormEvent) {
    event.preventDefault();
    const name = newLine.trim();
    if (!name) return;
    try {
      await upsertLine.mutateAsync({
        id: null,
        companyId,
        values: { name, description: '' },
        sortOrder: lines.length,
      });
      setNewLine('');
      toast.success(`קו המוצר "${name}" נוסף`);
    } catch (err) {
      toast.error(`לא הצלחנו להוסיף · ${errorMessage(err)}`);
    }
  }

  async function renameLine(line: { id: string; name: string }, name: string) {
    const trimmed = name.trim();
    if (!trimmed || trimmed === line.name) {
      setEditing(null);
      return;
    }
    try {
      await upsertLine.mutateAsync({
        id: line.id,
        companyId,
        values: {
          name: trimmed,
          description: lines.find((l) => l.id === line.id)?.description ?? '',
        },
      });
      setEditing(null);
      toast.success('השם עודכן');
    } catch (err) {
      toast.error(`לא הצלחנו לשמור · ${errorMessage(err)}`);
    }
  }

  async function removeLine(group: BookGroup) {
    if (!group.line) return;
    const n = group.projects.length;
    const detail = n > 0 ? ` ${String(n)} הפרויקטים שלו יישארו, בלי קו מוצר.` : '';
    if (!window.confirm(`למחוק את קו המוצר "${group.line.name}"?${detail}`)) return;
    try {
      await deleteLine.mutateAsync(group.line.id);
      toast.success('קו המוצר נמחק');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.bookHead}>
        <h2 className={styles.sectionTitle}>
          פרויקטים ({projects.length}) · קווי מוצר ({lines.length})
        </h2>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => {
            setDrawer({ open: true, lineId: null });
          }}
        >
          <Plus size={14} aria-hidden="true" />
          פרויקט חדש
        </button>
      </div>

      {projects.length > 0 ? <RollupLine rollup={book.total} /> : null}

      <form
        className={styles.lineForm}
        onSubmit={(e) => {
          void addLine(e);
        }}
      >
        <label htmlFor="new-line" className={styles.muted}>
          קו מוצר חדש ל{companyName}:
        </label>
        <input
          id="new-line"
          type="text"
          className={styles.lineInput}
          value={newLine}
          maxLength={120}
          onChange={(e) => {
            setNewLine(e.target.value);
          }}
        />
        <button
          type="submit"
          className={styles.btnSecondary}
          disabled={!newLine.trim() || upsertLine.isPending}
        >
          הוסף קו מוצר
        </button>
      </form>
      <p className={styles.notice}>
        קו מוצר הוא תחום שהלקוח קונה ממך, כמו "אתר", "קמפיינים" או "אפליקציה". כל קו מקבץ את
        הפרויקטים שלו, עם סיכום כסף ושעות.
      </p>

      {book.groups.length === 0 ? (
        <p className={styles.notice}>אין עדיין פרויקטים ללקוח זה.</p>
      ) : null}

      {book.groups.map((group) => {
        const key = group.line?.id ?? 'no-line';
        const isEditing = group.line !== null && editing?.id === group.line.id;
        return (
          <div
            key={key}
            className={cn(styles.lineGroup, group.line === null && styles.lineGroupLoose)}
          >
            <div className={styles.lineHead}>
              {isEditing && group.line ? (
                <form
                  className={styles.inlineAdd}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (group.line) void renameLine(group.line, editing.name);
                  }}
                >
                  <input
                    type="text"
                    className={styles.lineInput}
                    aria-label="שם קו המוצר"
                    value={editing.name}
                    maxLength={120}
                    autoFocus
                    onChange={(e) => {
                      setEditing({ id: editing.id, name: e.target.value });
                    }}
                  />
                  <button type="submit" className={styles.iconBtn} aria-label="שמור שם">
                    <Check size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    aria-label="בטל עריכה"
                    onClick={() => {
                      setEditing(null);
                    }}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </form>
              ) : (
                <h3 className={styles.lineTitle}>
                  {group.line ? group.line.name : 'ללא קו מוצר'}{' '}
                  <span className={styles.muted}>({group.projects.length})</span>
                </h3>
              )}
              <div className={styles.lineActions}>
                <button
                  type="button"
                  className={cn(styles.btnSecondary, styles.btnSmall)}
                  onClick={() => {
                    setDrawer({ open: true, lineId: group.line?.id ?? null });
                  }}
                >
                  <Plus size={12} aria-hidden="true" />
                  פרויקט {group.line ? `ב${group.line.name}` : ''}
                </button>
                {group.line && !isEditing ? (
                  <>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label={`שנה שם לקו המוצר ${group.line.name}`}
                      onClick={() => {
                        if (group.line) setEditing({ id: group.line.id, name: group.line.name });
                      }}
                    >
                      <Pencil size={13} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label={`מחק את קו המוצר ${group.line.name}`}
                      disabled={deleteLine.isPending}
                      onClick={() => {
                        void removeLine(group);
                      }}
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            {group.projects.length > 0 ? (
              <>
                <RollupLine rollup={group.rollup} />
                <div className={styles.tableWrap}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>פרויקט</th>
                        <th>סטטוס</th>
                        <th>ערך</th>
                        <th>התקבל</th>
                        <th>נשאר לקבל</th>
                        <th>שעות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.projects.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <Link to={`/projects/${p.id}`} className={styles.tableLink}>
                              {p.name}
                            </Link>
                          </td>
                          <td>
                            <span
                              className={cn(styles.projectStatus, styles[`status_${p.status}`])}
                            >
                              {projectStatusLabel(p.status)}
                            </span>
                          </td>
                          <td className={styles.numCell}>
                            <bdi>{formatILS(p.value_amount)}</bdi>
                          </td>
                          <td className={styles.numCell}>
                            <Link to={`/projects/${p.id}?tab=finance`} className={styles.tableLink}>
                              <bdi>{formatILS(p.received)}</bdi>
                            </Link>
                          </td>
                          <td className={styles.numCell}>
                            <bdi>{formatILS(p.outstanding)}</bdi>
                          </td>
                          <td className={styles.numCell}>
                            <Link to={`/projects/${p.id}?tab=hours`} className={styles.tableLink}>
                              <bdi>{formatDuration(p.minutes)}</bdi>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className={styles.notice}>אין עדיין פרויקטים בקו הזה.</p>
            )}
          </div>
        );
      })}

      <ProjectDrawer
        open={drawer.open}
        project={null}
        companies={[{ id: companyId, name: companyName }]}
        defaultCompanyId={companyId}
        {...(drawer.lineId ? { defaultProductLineId: drawer.lineId } : {})}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setDrawer({ open: false, lineId: null });
        }}
      />
    </section>
  );
}
